import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';

// Create the Python context
const PythonContext = createContext(null);

/**
 * Provider component that manages a single shared Python (Pyodide) worker instance
 * This ensures only one Pyodide WASM instance is loaded, improving performance
 */
export const PythonProvider = ({ children }) => {
  const [isReady, setIsReady] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const workerRef = useRef(null);
  const executionIdRef = useRef(0);
  const callbacksRef = useRef({});
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize worker only when explicitly requested
  const initializeWorker = useCallback(() => {
    if (isInitialized || workerRef.current) {
      return; // Already initialized
    }

    console.log('[PythonContext] Initializing Python worker...');

    // Check for stale execution state from previous session/reload
    // If execution was in progress before reload, reset the state
    const wasExecuting = sessionStorage.getItem('python_executing');
    if (wasExecuting === 'true') {
      console.log('[PythonContext] Detected stale execution state, clearing...');
      sessionStorage.removeItem('python_executing');
    }

    setIsInitialized(true);

    // Create the worker
    const worker = new Worker('/workers/python-worker.js');
    workerRef.current = worker;

    // Handle messages from the worker
    worker.onmessage = (e) => {
      const { type, id, data, result, error, executionTimeMs } = e.data;

      switch (type) {
        case 'ready':
          console.log('[PythonContext] Python worker ready');
          setIsReady(true);
          break;

        case 'stdout':
          if (callbacksRef.current[id]?.onStdout) {
            callbacksRef.current[id].onStdout(data);
          }
          break;

        case 'stderr':
          if (callbacksRef.current[id]?.onStderr) {
            callbacksRef.current[id].onStderr(data);
          }
          break;

        case 'artifacts':
          if (callbacksRef.current[id]?.onArtifacts) {
            callbacksRef.current[id].onArtifacts(data);
          }
          break;

        case 'plot_stream':
          // Handle streaming plots as they're generated
          if (callbacksRef.current[id]?.onPlotStream) {
            callbacksRef.current[id].onPlotStream(data);
          }
          break;

        case 'complete':
          setIsExecuting(false);
          sessionStorage.removeItem('python_executing');
          if (callbacksRef.current[id]?.onComplete) {
            callbacksRef.current[id].onComplete(result, executionTimeMs);
          }
          delete callbacksRef.current[id];
          break;

        case 'error':
          setIsExecuting(false);
          sessionStorage.removeItem('python_executing');
          if (callbacksRef.current[id]?.onError) {
            callbacksRef.current[id].onError(error, executionTimeMs);
          }
          delete callbacksRef.current[id];
          break;

        default:
          console.warn('[PythonContext] Unknown message type from worker:', type);
      }
    };

    worker.onerror = (error) => {
      console.error('[PythonContext] Worker error:', error);
      setIsReady(false);
      setIsExecuting(false);
    };
  }, [isInitialized]);

  // Cleanup worker on unmount
  useEffect(() => {
    return () => {
      if (workerRef.current) {
        console.log('[PythonContext] Terminating Python worker');
        workerRef.current.terminate();
      }
    };
  }, []);

  const execute = useCallback((code, callbacks = {}) => {
    if (!isReady) {
      callbacks.onError?.('Python is not ready yet');
      return;
    }

    if (isExecuting) {
      callbacks.onError?.('Another execution is in progress');
      return;
    }

    const id = ++executionIdRef.current;
    callbacksRef.current[id] = callbacks;
    setIsExecuting(true);
    sessionStorage.setItem('python_executing', 'true');

    workerRef.current.postMessage({
      type: 'execute',
      code,
      id
    });

    return id;
  }, [isReady, isExecuting]);

  // Cancel current execution by terminating and recreating the worker
  const cancelExecution = useCallback(() => {
    if (!isExecuting) return;

    console.log('[PythonContext] Cancelling execution...');

    // Terminate the current worker
    if (workerRef.current) {
      workerRef.current.terminate();
      workerRef.current = null; // Clear the ref so initializeWorker can create a new one
    }

    // Clear all pending callbacks
    Object.keys(callbacksRef.current).forEach(id => {
      callbacksRef.current[id]?.onError?.('Execution cancelled by user');
      delete callbacksRef.current[id];
    });

    // Reset state
    setIsExecuting(false);
    sessionStorage.removeItem('python_executing');
    setIsReady(false);

    // Reinitialize the worker
    setIsInitialized(false);
    setTimeout(() => {
      initializeWorker();
    }, 100);
  }, [isExecuting, initializeWorker]);

  // Force reset execution state (for recovery from stuck states)
  const resetExecutionState = useCallback(() => {
    console.log('[PythonContext] Force resetting execution state...');
    setIsExecuting(false);

    // Clear all pending callbacks
    Object.keys(callbacksRef.current).forEach(id => {
      delete callbacksRef.current[id];
    });
  }, []);

  const value = {
    isReady,
    isExecuting,
    isInitialized,
    execute,
    cancelExecution,
    resetExecutionState,
    initializeWorker
  };

  return (
    <PythonContext.Provider value={value}>
      {children}
    </PythonContext.Provider>
  );
};

/**
 * Hook to access the shared Python worker
 * Automatically initializes the worker on first use if not already initialized
 */
export const usePython = () => {
  const context = useContext(PythonContext);

  if (!context) {
    throw new Error('usePython must be used within a PythonProvider');
  }

  // Auto-initialize when hook is first used
  useEffect(() => {
    if (!context.isInitialized) {
      console.log('[usePython] Auto-initializing Python worker');
      context.initializeWorker();
    }
  }, [context]);

  return {
    isReady: context.isReady,
    isExecuting: context.isExecuting,
    execute: context.execute,
    cancelExecution: context.cancelExecution,
    resetExecutionState: context.resetExecutionState
  };
};
